import React, { useCallback, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { isDatasetIdentifier } from "utils/utils";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";
import useChartDialogStore from "store/chart-dialog-store";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { T } from "services/lang/LanguageService";
import { MappedValue } from "components/basic/dataset-display-value/DatasetDisplayValue";
import useCopyDataset from "hooks/useCopyDataset";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";

type DatasetProps = {
  values: string | string[] | null;
  changeHandlerMulti?: (receivedValue: string[] | null) => void;
  changeHandler?: (receivedValue: string | null) => void;
  isInEditMode: boolean;
  decimalPlaces?: number;
  isSingleDataset?: boolean;
};

const Dataset: React.FC<DatasetProps> = ({
  changeHandler,
  changeHandlerMulti,
  values,
  isInEditMode,
  decimalPlaces,
  isSingleDataset,
}) => {
  const getCurrentValues = (): string | string[] | null => {
    if (typeof values === "string") {
      return isSingleDataset ? values : [values];
    }
    return values;
  };

  const [dialogVisible, setDialogVisible] = useState(false);
  const [currentDatasets, setCurrentDatasets] = useState<string | string[] | null>(
    getCurrentValues());
  const handleDatasetsChange = useCallback((newDatasets: string[] | string | null) => {
    setCurrentDatasets(newDatasets);
  }, []);

  const handleDatasetsSave = useCallback(() => {
    if (isSingleDataset && changeHandler) {
      changeHandler(currentDatasets as string | null);
    } else if (changeHandlerMulti) {
      changeHandlerMulti(currentDatasets as string[]);
    }
    setDialogVisible(false);
  }, [changeHandler, changeHandlerMulti, isSingleDataset, currentDatasets]);

  const dialogFooterTemplate = useCallback(() => {
    return (
      <div className="flex justify-content-end gap-2 mt-3">
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={() => setDialogVisible(false)}
          className="component-editor-button icon-red"
        />
        <Button
          type="button"
          label={T("action.save")}
          icon="pi pi-check"
          onClick={handleDatasetsSave}
          className="component-editor-button icon-green"
        />
      </div>
    );
  }, [handleDatasetsSave]);

  if (isInEditMode) {
    return (
      <>
        <Button
          label={isSingleDataset ? T("word.dataset") : T("word.datasets")}
          className="bg-gray-200 border-none text-black-alpha-90 hover:bg-gray-300 h-2rem"
          icon="pi pi-list"
          onClick={() => setDialogVisible(true)}
        />
        <Dialog
          header={isSingleDataset ? T("action.select-dataset") : T("action.select-datasets")}
          visible={dialogVisible}
          style={{ width: "40rem" }}
          footer={dialogFooterTemplate}
          modal
          onHide={() => setDialogVisible(false)}
        >
          {isSingleDataset ? (
            <DatasetDropdown value={currentDatasets as string} onChange={handleDatasetsChange} />
          ) : (
            <DatasetMultiSelect
              value={currentDatasets as string[]}
              onChange={handleDatasetsChange}
            />
          )}
        </Dialog>
      </>
    );
  }

  const getNormalizedValues = () => {
    if (Array.isArray(values)) {
      return values;
    }
    return values ? [values] : [];
  };

  const datasets = getNormalizedValues().map(value => (isDatasetIdentifier(value) ? value : null));

  if (datasets.length === 0) return "-";

  return (
    <div>
      {datasets?.map(
        dataset =>
          dataset && (
            <DatasetDisplayWrapper
              key={dataset}
              identifier={dataset}
              decimalPlaces={decimalPlaces}
            />
          )
      )}
    </div>
  );
};

const DatasetDisplayWrapper = ({
  identifier,
  decimalPlaces,
}: {
  identifier: string;
  decimalPlaces?: number;
}) => {
  const { data, isLoading } = useGetRecentData([identifier!], {
    options: { enabled: !!identifier },
  });

  if (isLoading) return <div className="pi pi-spin pi-spinner" />;

  const value = data?.[0].data?.[0]?.value;
  const unit = data?.[0].unit;
  const stringValueMapper = data?.[0].stringValueMapper;
  const title = data?.[0].title;

  if (value == null && !isLoading) return <>&#8208;</>;

  return (
    <DatasetDisplay
      identifier={identifier}
      value={value}
      unit={unit}
      title={title}
      stringValueMapper={stringValueMapper}
      decimalPlaces={decimalPlaces}
    />
  );
};

const DatasetDisplay = ({
  identifier,
  value,
  unit,
  title,
  stringValueMapper,
  decimalPlaces,
}: {
  identifier: string;
  value?: number;
  unit?: string;
  title?: string;
  stringValueMapper?: number;
  decimalPlaces?: number;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  useCopyDataset(isHovered, title, identifier);

  const selectedIdsStore = useDatasetIdentifiersStore();
  const { openDialog } = useChartDialogStore();

  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey) {
      if (selectedIdsStore.ids.has(identifier)) {
        selectedIdsStore.removeIdentifier(identifier);
      } else {
        selectedIdsStore.addIdentifier(identifier);
      }
    } else {
      selectedIdsStore.clear();
    }
  };

  const handleDoubleClick = () => {
    selectedIdsStore.addIdentifier(identifier);
    openDialog();
  };

  const isIdentifierSelected = selectedIdsStore.ids.has(identifier);
  return (
    <div
      className="p-inputtext"
      style={{
        border: isIdentifierSelected ? "1px solid #007df2" : "none",
        borderRadius: "0px",
        backgroundColor: "inherit",
        color: "inherit",
        whiteSpace: "pre-wrap",
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      {stringValueMapper ? (
        <MappedValue mapValue={value} mapperId={stringValueMapper} />
      ) : (
        <DatasetDisplayValue value={value} unit={unit} decimalPlaces={decimalPlaces} />
      )}
    </div>
  );
};

export default Dataset;

const DatasetDisplayValue = ({
  value,
  unit,
  decimalPlaces,
}: {
  value: number | undefined;
  unit: string | undefined;
  decimalPlaces?: number;
}) => {
  return (
    value && (
      <>
        {`${value.toFixed(decimalPlaces ?? 2)} ${unit}`}
      </>
    )
  );
};
